import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  CreditCard, 
  Archive, 
  RefreshCw, 
  XCircle,
  Clock,
  CheckCheck,
  Loader2,
  Eye,
  FileText,
  Calendar,
  Gift,
  FileSignature,
  ClipboardList,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { differenceInDays } from "date-fns";
import { ReplaceDocumentDialog } from "./ReplaceDocumentDialog";
import { CreateDemarcheFromActionDialog } from "./CreateDemarcheFromActionDialog";


export type ActionType = 'pay' | 'archive' | 'renew' | 'verify' | 'cancel' | 'expiring';
export type Priority = 'urgent' | 'high' | 'medium' | 'low';
export type DateContext = 'expiration' | 'event' | 'deadline' | 'end_commitment' | 'reference';
export type ActionRequired = 'renew' | 'attend' | 'use' | 'pay' | 'review';

export interface FineData {
  amount: number | null;
  reducedAmount: number | null;
  reducedDeadline: string | null;
  normalDeadline: string | null;
  reference: string | null;
}

export interface ActionItem {
  id: string;
  type: 'action' | 'expiring';
  actionType: ActionType;
  title: string;
  documentTitle?: string;
  documentId: string;
  docType?: string | null;
  priority?: Priority;
  dueDate?: string;
  expiresAt?: string;
  daysUntilExpiry?: number;
  filePath?: string;
  mimeType?: string | null;
  dateContext?: DateContext | string | null;
  actionRequired?: ActionRequired | string | null;
  isManual?: boolean;
  linkedDemarche?: {
    id: string;
    title: string;
    status: string;
  };
  // New fields for zero-false-negative system
  fineData?: FineData;
  isFreshness?: boolean;
  showAlreadyPaid?: boolean;
  score?: number;
}

interface ActionCardProps {
  item: ActionItem;
  onComplete: (id: string) => void;
  onDismiss: (id: string) => void;
  onView: (item: ActionItem) => void;
  isUpdating?: boolean;
  onAlreadyPaid?: (id: string) => void;
}

const ACTION_CONFIG: Record<ActionType, { 
  icon: React.ElementType; 
  color: string; 
  bgColor: string;
  label: string;
}> = {
  pay: { 
    icon: CreditCard, 
    color: "text-destructive", 
    bgColor: "bg-destructive/10",
    label: "À payer"
  },
  archive: { 
    icon: Archive, 
    color: "text-muted-foreground", 
    bgColor: "bg-muted",
    label: "À archiver"
  },
  renew: { 
    icon: RefreshCw, 
    color: "text-warning", 
    bgColor: "bg-warning/10",
    label: "À renouveler"
  },
  verify: { 
    icon: Calendar, 
    color: "text-primary", 
    bgColor: "bg-primary/10",
    label: "Événement"
  },
  cancel: { 
    icon: FileSignature, 
    color: "text-purple-500", 
    bgColor: "bg-purple-500/10",
    label: "Fin engagement"
  },
  expiring: { 
    icon: Clock, 
    color: "text-warning", 
    bgColor: "bg-warning/10",
    label: "Expire bientôt"
  },
};

// Context-aware configuration for date types
const DATE_CONTEXT_CONFIG: Record<string, { 
  icon: React.ElementType; 
  color: string; 
  bgColor: string;
  label: string;
  verb: string;
}> = {
  expiration: {
    icon: RefreshCw,
    color: "text-warning",
    bgColor: "bg-warning/10",
    label: "À renouveler",
    verb: "expire"
  },
  event: {
    icon: Calendar,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    label: "Événement",
    verb: "a lieu"
  },
  deadline: {
    icon: Gift,
    color: "text-orange-500",
    bgColor: "bg-orange-500/10",
    label: "À utiliser",
    verb: "valable jusqu'au"
  },
  end_commitment: {
    icon: FileSignature,
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
    label: "Fin engagement",
    verb: "se termine"
  },
  technical_inspection: {
    icon: Calendar,
    color: "text-amber-600",
    bgColor: "bg-amber-100",
    label: "Contrôle technique",
    verb: "à prévoir avant le"
  }
};

const PRIORITY_CONFIG: Record<Priority, { label: string; className: string }> = {
  urgent: { label: "Urgent", className: "bg-destructive text-destructive-foreground" },
  high: { label: "Important", className: "bg-warning text-warning-foreground" },
  medium: { label: "Normal", className: "bg-primary/10 text-primary" },
  low: { label: "Optionnel", className: "bg-muted text-muted-foreground" },
};

export function ActionCard({ item, onComplete, onDismiss, onView, isUpdating, onAlreadyPaid }: ActionCardProps) {
  const navigate = useNavigate();
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [showDemarcheDialog, setShowDemarcheDialog] = useState(false);
  
  // Use date context config if available, otherwise fall back to action config
  const dateContextConfig = item.dateContext && DATE_CONTEXT_CONFIG[item.dateContext];
  const config = dateContextConfig || ACTION_CONFIG[item.actionType] || ACTION_CONFIG.expiring;
  const IconComponent = config.icon;
  
  const isExpired = item.daysUntilExpiry !== undefined && item.daysUntilExpiry < 0;
  const isUrgent = item.priority === 'urgent' || item.priority === 'high' || isExpired;
  const isFine = item.id.startsWith('fine-');
  const showPaymentActions = item.showAlreadyPaid && !isFine;

  // Show "Replace" and "Create Demarche" for expiring documents (not manual actions, not fines, not payments)
  const showQuickActions = item.type === 'expiring' && !item.isManual && !isFine && !showPaymentActions;
  // Hide "Create Demarche" button if a demarche already exists
  const hasDemarche = !!item.linkedDemarche;

  // Calculate urgency progress (days remaining visualization)
  const getUrgencyProgress = () => {
    if (item.daysUntilExpiry !== undefined) {
      if (item.daysUntilExpiry < 0) return 100;
      if (item.daysUntilExpiry <= 7) return 80;
      if (item.daysUntilExpiry <= 30) return 50;
      return 20;
    }
    if (item.dueDate) {
      const days = differenceInDays(new Date(item.dueDate), new Date());
      if (days < 0) return 100;
      if (days <= 3) return 80;
      if (days <= 7) return 50;
      return 20;
    }
    return 0;
  };

  // Context-aware deadline formatting
  const formatDeadline = () => {
    const verb = dateContextConfig?.verb || "expire";
    
    if (item.daysUntilExpiry !== undefined) {
      if (item.daysUntilExpiry < 0) {
        if (item.dateContext === 'event') return `Événement passé (${Math.abs(item.daysUntilExpiry)}j)`;
        if (item.dateContext === 'technical_inspection') return `En retard de ${Math.abs(item.daysUntilExpiry)}j`;
        return `Expiré depuis ${Math.abs(item.daysUntilExpiry)}j`;
      }
      if (item.daysUntilExpiry === 0) {
        if (item.dateContext === 'event') return "Aujourd'hui !";
        if (item.dateContext === 'technical_inspection') return "Contrôle à faire aujourd'hui";
        return "Expire aujourd'hui";
      }
      if (item.daysUntilExpiry === 1) {
        if (item.dateContext === 'event') return "Demain";
        if (item.dateContext === 'technical_inspection') return "À faire demain";
        return "Expire demain";
      }
      if (item.dateContext === 'event') return `Dans ${item.daysUntilExpiry}j`;
      if (item.dateContext === 'deadline') return `Valable encore ${item.daysUntilExpiry}j`;
      if (item.dateContext === 'end_commitment') return `Se termine dans ${item.daysUntilExpiry}j`;
      if (item.dateContext === 'technical_inspection') return `À prévoir dans ${item.daysUntilExpiry}j`;
      return `Expire dans ${item.daysUntilExpiry}j`;
    }
    if (item.dueDate) {
      const days = differenceInDays(new Date(item.dueDate), new Date());
      if (days < 0) return `${Math.abs(days)}j en retard`;
      if (days === 0) return "Aujourd'hui";
      if (days === 1) return "Demain";
      if (days <= 7) return `Dans ${days}j`;
      return new Date(item.dueDate).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
    }
    return null;
  };

  const deadline = formatDeadline();
  const urgencyProgress = getUrgencyProgress();

  const handleReplaceSuccess = () => {
    // Refresh the list by calling onComplete (will remove item from list)
    onComplete(item.id);
  };

  const handleDemarcheSuccess = () => {
    // The item will be removed via onComplete or the dialog handles navigation
    onComplete(item.id);
  };

  return (
    <>
      <motion.div
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -100, height: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Card 
          className={cn(
            "overflow-hidden transition-all hover:shadow-md",
            isFine && "border-destructive bg-destructive/5",
            isExpired && !isFine && item.dateContext !== 'event' && "border-destructive/50 bg-destructive/5",
            !isExpired && !isFine && isUrgent && "border-warning/50 bg-warning/5",
            item.dateContext === 'event' && "border-primary/50 bg-primary/5",
            item.dateContext === 'end_commitment' && "border-accent/50 bg-accent/5",
            item.isFreshness && "border-warning/30 bg-warning/5"
          )}
        >
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className={cn(
                "w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-transform hover:scale-105",
                isExpired && item.dateContext !== 'event' ? "bg-destructive/10" : config.bgColor
              )}>
                <IconComponent className={cn(
                  "w-6 h-6",
                  isExpired && item.dateContext !== 'event' ? "text-destructive" : config.color
                )} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-foreground truncate">
                      {item.title}
                    </p>
                    <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                      {item.isManual ? (
                        <>
                          <ClipboardList className="w-3 h-3" />
                          <span className="italic">Action manuelle</span>
                        </>
                      ) : (
                        <>
                          <FileText className="w-3 h-3" />
                          {item.documentTitle || "Document"}
                        </>
                      )}
                    </p>
                  </div>
                  
                  {/* Priority badge */}
                  {item.priority && (
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px] px-2 shrink-0",
                        PRIORITY_CONFIG[item.priority].className
                      )}
                    >
                      {PRIORITY_CONFIG[item.priority].label}
                    </Badge>
                  )}
                </div>

                {/* Linked demarche badge */}
                {item.linkedDemarche && (
                  <Badge 
                    variant="outline" 
                    className="w-fit bg-primary/10 text-primary border-primary/20 cursor-pointer hover:bg-primary/20 transition-colors gap-1"
                    onClick={() => navigate(`/demarches/${item.linkedDemarche!.id}`)}
                  >
                    <ClipboardList className="w-3 h-3" />
                    Démarche en cours
                  </Badge>
                )}

                {/* Deadline with progress */}
                {deadline && (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className={cn(
                        "text-xs flex items-center gap-1",
                        isExpired ? "text-destructive font-medium" : 
                        urgencyProgress >= 80 ? "text-warning" : "text-muted-foreground"
                      )}>
                        <Clock className="w-3 h-3" />
                        {deadline}
                      </span>
                    </div>
                    <Progress 
                      value={urgencyProgress} 
                      className={cn(
                        "h-1",
                        isExpired ? "[&>div]:bg-destructive" :
                        urgencyProgress >= 80 ? "[&>div]:bg-warning" :
                        "[&>div]:bg-primary"
                      )}
                    />
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center gap-2 pt-1 flex-wrap">
                  {/* Payment actions for invoices */}
                  {showPaymentActions && (
                    <>
                      <Button
                        variant="default"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => onComplete(item.id)}
                        disabled={isUpdating}
                      >
                        <CreditCard className="w-3 h-3 mr-1" />
                        Payer
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => onAlreadyPaid?.(item.id)}
                        disabled={isUpdating}
                      >
                        <CheckCheck className="w-3 h-3 mr-1" />
                        Déjà payé
                      </Button>
                    </>
                  )}

                  {/* Fine actions */}
                  {isFine && (
                    <>
                      <Button
                        variant="default"
                        size="sm"
                        className="h-8 text-xs bg-destructive hover:bg-destructive/90"
                        onClick={() => onComplete(item.id)}
                        disabled={isUpdating}
                      >
                        <CreditCard className="w-3 h-3 mr-1" />
                        Payer l'amende
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => onView(item)}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Détails
                      </Button>
                    </>
                  )}

                  {/* Quick actions for expiring documents */}
                  {showQuickActions && (
                    <>
                      <Button
                        variant="default"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => setShowReplaceDialog(true)}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Remplacer
                      </Button>
                      {/* Only show "Create Demarche" if no demarche exists yet */}
                      {!hasDemarche && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => setShowDemarcheDialog(true)}
                        >
                          <ClipboardList className="w-3 h-3 mr-1" />
                          Créer démarche
                        </Button>
                      )}
                    </>
                  )}

                  {/* View button for all non-manual, non-fine actions (including payments) */}
                  {!item.isManual && !isFine && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => onView(item)}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Voir
                    </Button>
                  )}
                  
                  {/* Create démarche for payment actions */}
                  {showPaymentActions && !hasDemarche && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => setShowDemarcheDialog(true)}
                    >
                      <ClipboardList className="w-3 h-3 mr-1" />
                      Créer démarche
                    </Button>
                  )}

                  {/* Dismiss button - not for fines, not for payment actions (already have "Déjà payé") */}
                  {!isFine && !showPaymentActions && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => onDismiss(item.id)}
                      disabled={isUpdating}
                    >
                      {isUpdating ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <XCircle className="w-3 h-3 mr-1" />
                      )}
                      Ignorer
                    </Button>
                  )}

                  {/* Complete/Done button - only show for manual actions */}
                  {!showQuickActions && !isFine && !showPaymentActions && (
                    <Button
                      size="sm"
                      className="h-8 text-xs ml-auto bg-success hover:bg-success/90 text-success-foreground"
                      onClick={() => onComplete(item.id)}
                      disabled={isUpdating}
                    >
                      {isUpdating ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <CheckCheck className="w-3 h-3 mr-1" />
                      )}
                      {item.type === 'expiring' ? 'Renouvelé' : 'Fait'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Replace Document Dialog - only for expiring documents */}
      {showQuickActions && (
        <ReplaceDocumentDialog
          open={showReplaceDialog}
          onOpenChange={setShowReplaceDialog}
          originalDocument={{
            id: item.documentId,
            title: item.documentTitle || item.title,
            doc_type: item.docType || null,
            file_path: item.filePath,
          }}
          onSuccess={handleReplaceSuccess}
        />
      )}

      {/* Create Demarche Dialog - available for both expiring docs and payments */}
      {(showQuickActions || showPaymentActions) && (
        <CreateDemarcheFromActionDialog
          open={showDemarcheDialog}
          onOpenChange={setShowDemarcheDialog}
          document={{
            id: item.documentId,
            title: item.documentTitle || item.title,
            doc_type: item.docType || null,
          }}
          onSuccess={handleDemarcheSuccess}
        />
      )}
    </>
  );
}
